import { EnumerationsExportStatus } from 'client'
import type { NotificationsNotificationDTO } from 'client'
import type { IReportExportType } from '~/constants/types/reportSettings'
import { useSignalREvents } from '~/composables/signalR/useSignalR'
import { useAAPExportReportsStore } from '~/stores/AAPExportReports'
import { useNotificationsStore } from '~/stores/notifications'
import { SIGNALR_EVENTS } from '~/constants/signalREvents'

export const useReportExport = (): IReportExportType => {
  const signalR = useSignalREvents()
  const NotificationsStore = useNotificationsStore()
  const { reportBeingExported, exportDialogOpen, isDownloading } = storeToRefs(
    useAAPExportReportsStore()
  )
  const AAPExportReportsStore = useAAPExportReportsStore()

  // Added related to elimination of EnumerationExportStatus from signalR notifications
  // (Removed as part of move of signalR to separate project)
  const ExportStatusStringToEnumerationExportStatus: Record<
    keyof typeof EnumerationsExportStatus,
    EnumerationsExportStatus
  > = {
    InProgress: EnumerationsExportStatus.InProgress,
    Cancelled: EnumerationsExportStatus.Cancelled,
    Failed: EnumerationsExportStatus.Failed,
    Completed: EnumerationsExportStatus.Completed,
  } as const

  const handleExportProgress = async (
    notificationID: string,
    reportDownloadID: string,
    percentage: number,
    exportStatus: keyof typeof ExportStatusStringToEnumerationExportStatus,
    reportName: string,
    establishment: string,
    summaryName: string,
    isSummaryExport: boolean
  ) => {
    const updateExport = (
      notification: NotificationsNotificationDTO,
      percentage: number,
      currentStepDescription: string
    ) => {
      notification.exportProgressInfo.currentStepDescription =
        currentStepDescription
      notification.exportProgressInfo.percent =
        percentage ||
        notification.exportProgressInfo.currentStep /
          notification.exportProgressInfo.totalSteps
      notification.exportProgressInfo.exportStatus =
        percentage === 1
          ? EnumerationsExportStatus.Completed
          : ExportStatusStringToEnumerationExportStatus[exportStatus]
    }

    // for updating dropdown notifications
    const familiarDropdownExport =
      NotificationsStore.dropdownNotifications.find(
        (n) => n.notificationID === notificationID
      )
    const familiarPageExport = NotificationsStore.allNotifications.find(
      (n) => n.notificationID === notificationID
    )

    const isExportComplete =
      ExportStatusStringToEnumerationExportStatus[exportStatus] ===
        EnumerationsExportStatus.Completed || reportName === 'Complete'
    const formattedPercentage = isExportComplete ? 1 : percentage || 0

    const currentStepDescription = `Export at ${Math.round(
      (formattedPercentage || 0) * 100
    )}% complete. ${
      reportName
        ? `Current report: ${
            establishment ? `${establishment} - ` : ''
          }${reportName}`
        : ''
    }`

    // only increment percent if is the currently selected export
    if (notificationID === reportBeingExported.value?.notificationID) {
      reportBeingExported.value.percent = formattedPercentage
      reportBeingExported.value.exportStatus =
        ExportStatusStringToEnumerationExportStatus[exportStatus]
      reportBeingExported.value.currentStepDescription = currentStepDescription
      reportBeingExported.value.reportDownloadID = reportDownloadID
      reportBeingExported.value.failed =
        ExportStatusStringToEnumerationExportStatus[exportStatus] ===
        EnumerationsExportStatus.Failed
      reportBeingExported.value.isSummaryExport = isSummaryExport
      reportBeingExported.value.summaryName = summaryName
    }

    if (familiarDropdownExport) {
      updateExport(
        familiarDropdownExport,
        formattedPercentage,
        currentStepDescription
      )
    }

    if (familiarPageExport) {
      updateExport(
        familiarPageExport,
        formattedPercentage,
        currentStepDescription
      )
    }

    if (
      isExportComplete &&
      reportBeingExported.value?.reportDownloadID === reportDownloadID
    ) {
      const skipAutomaticDownload =
        !exportDialogOpen.value ||
        !reportBeingExported.value ||
        !reportBeingExported.value.notificationID ||
        isDownloading.value ||
        !reportBeingExported.value.reportDownloadID

      if (skipAutomaticDownload) {
        return
      }

      isDownloading.value = true

      await AAPExportReportsStore.onDownloadExport(
        reportBeingExported.value.reportDownloadID
      )

      reportBeingExported.value.percent = 100
      reportBeingExported.value.exportStatus =
        EnumerationsExportStatus.Completed

      // for UX purposes
      setTimeout(() => {
        isDownloading.value = false
        AAPExportReportsStore.clearExportData()
      }, 600)
    }
  }

  const handleExportFailure = async (notificationID: string) => {
    if (reportBeingExported.value) {
      const currentExportInDialog = exportDialogOpen.value
        ? notificationID === reportBeingExported.value?.notificationID
        : false
      if (currentExportInDialog) {
        reportBeingExported.value.failed = true
      }
    }
    await NotificationsStore.getDropdownNotifications()
  }

  const handleOff = () => {
    signalR.off(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_EXPORT_PROGRESS,
      handleExportProgress
    )
    signalR.off(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_EXPORT_FAILED,
      handleExportFailure
    )
  }
  const handleOn = () => {
    signalR.on(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_EXPORT_PROGRESS,
      handleExportProgress
    )
    signalR.on(
      SIGNALR_EVENTS.EXPORT_HUB.NOTIFY_EXPORT_FAILED,
      handleExportFailure
    )
  }

  onUnmounted(() => {
    handleOff()
  })

  return {
    handleOff,
    handleOn,
  }
}
